import {
  EMIR_RESPONSE_MODEL_DETAILED,
  EMIR_RESPONSE_MODEL_STANDARD,
  type EmirBreakdownPayload,
  type EmirMancoSectionSummary,
  type EmirOverviewPageModel,
  type EmirResponseModel,
} from './types';

/** Canonical manco pie order (matches backend ``MANCO_LIMIT`` / legacy ``pass_fails``). */
export const EMIR_MANCO_SECTION_ORDER = [
  'interest_rate_derivatives',
  'credit_derivatives',
  'equity_derivatives',
  'commodity_derivatives',
  'fx_derivatives',
] as const;

export function getEmirResponseModel(
  payload: EmirBreakdownPayload | null | undefined,
): EmirResponseModel {
  if (payload?.emir_response_model === EMIR_RESPONSE_MODEL_DETAILED) {
    return EMIR_RESPONSE_MODEL_DETAILED;
  }
  return EMIR_RESPONSE_MODEL_STANDARD;
}

export function getEmirTableExportFileName(
  tableKey: string,
  positionDate: string,
): string {
  return `EMIR_${tableKey}-${positionDate}`;
}

/** Latest resolved position date from the overview envelope — for display only, not the picker selection. */
export function getEmirReturnedPositionDate(
  pageModel: EmirOverviewPageModel | null | undefined,
): string | null {
  const dates = pageModel?.returned_position_dates?.filter(Boolean) ?? [];
  if (!dates.length) {
    return null;
  }
  // Overview pages always omit `returned_position_date`; dates are per-fund in
  // `returned_position_dates`. Show the latest resolved date across funds.
  return dates.reduce((latest, date) => (date > latest ? date : latest));
}

export function isDetailedEmirPayload(
  payload: EmirBreakdownPayload | null | undefined,
): boolean {
  return getEmirResponseModel(payload) === EMIR_RESPONSE_MODEL_DETAILED;
}

/** Stable manco pie order: ``manco_limits`` keys when present, else ``EMIR_MANCO_SECTION_ORDER``. */
export function sortEmirMancoSectionSummaries(
  sections: EmirMancoSectionSummary[],
  sectionOrder?: readonly string[],
): EmirMancoSectionSummary[] {
  const order =
    sectionOrder && sectionOrder.length > 0
      ? sectionOrder
      : EMIR_MANCO_SECTION_ORDER;
  const orderIndex = new Map(order.map((key, index) => [key, index]));

  return [...sections].sort((a, b) => {
    const aIndex = orderIndex.get(a.key) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = orderIndex.get(b.key) ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
}
